import { useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { caseDisplayName } from "@/lib/caseName";

export function CaseCombobox({ cases = [], value, onChange, placeholder = "Link to a case (optional)" }) {
  const [open, setOpen] = useState(false);
  const selected = cases.find((c) => c.case_id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="case-combobox-trigger"
          className="w-full justify-between bg-charcoal border-white/15 text-white hover:bg-charcoal hover:text-white font-normal"
        >
          <span className={selected ? "text-white" : "text-white/40"}>
            {selected ? caseDisplayName(selected.title) : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-surface border-white/10 text-white" align="start">
        <Command
          className="bg-surface text-white"
          filter={(itemValue, search) => {
            const q = search.toLowerCase().replace(/^the\s+/i, "").trim();
            if (!q) return 1;
            return itemValue.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <div className="flex items-center border-b border-white/10 px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Search by name or defendant..."
              className="h-10 bg-transparent text-white placeholder:text-white/40 border-0 focus:ring-0"
              data-testid="case-combobox-search"
            />
          </div>
          <CommandList className="max-h-64">
            <CommandEmpty className="py-4 text-center text-sm text-white/40">No matching case.</CommandEmpty>
            <CommandItem
              value="none no-case"
              onSelect={() => { onChange(""); setOpen(false); }}
              className="text-white/70 aria-selected:bg-white/10"
              data-testid="case-option-none"
            >
              <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100 text-gold" : "opacity-0"}`} />
              No case
            </CommandItem>
            {cases.map((c) => (
              <CommandItem
                key={c.case_id}
                value={`${c.title} ${c.aliases || ""} ${c.case_id}`}
                onSelect={() => { onChange(c.case_id); setOpen(false); }}
                className="text-white aria-selected:bg-white/10 cursor-pointer"
                data-testid={`case-option-${c.case_id}`}
              >
                <Check className={`mr-2 h-4 w-4 ${value === c.case_id ? "opacity-100 text-gold" : "opacity-0"}`} />
                <span className="truncate">{caseDisplayName(c.title)}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
